/* eslint-disable no-param-reassign */
import { useLocation, useHistory } from 'react-router-dom';
import { useReducer, useEffect, useState } from 'react';
import mitt from 'mitt';
import defaultsDeep from 'lodash/defaultsDeep';
import useEventCallback from './useEventCallback';

function parseQueryString(qs) {
  const search = new URLSearchParams(qs);
  const state = {};
  Array.from(search.entries()).forEach(([k, v]) => {
    state[k] = v.includes(',') ? v.split(',') : v;
  });
  return state;
}

function partialStateReducer(state, updater) {
  const newPartialState =
    typeof updater === 'function' ? updater(state) : updater;
  return {
    ...state,
    ...newPartialState,
  };
}

function useGlobalState(getState, setState, updateFn, listen = () => {}) {
  const [, forceRender] = useReducer(s => s + 1, 0);
  const dispatch = useEventCallback(action => {
    const state = getState();
    const newState = updateFn(state, action);
    setState(newState);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => listen(forceRender), []);
  return [getState(), dispatch];
}

function useQueryStringState(initialState = {}) {
  return useQueryStringReducer(partialStateReducer, initialState);
}

function useQueryStringReducer(reducer, initialState = {}) {
  const location = useLocation();
  const history = useHistory();

  function get() {
    return defaultsDeep(parseQueryString(location.search), initialState);
  }

  function set(state) {
    if (Object.values(state))
      history.replace({ search: new URLSearchParams(state).toString() });
  }

  return useGlobalState(get, set, reducer);
}

function useHistoryState(initialState = {}) {
  return useHistoryReducer(partialStateReducer, initialState);
}

function useHistoryReducer(reducer, initialState = {}) {
  const location = useLocation();
  const history = useHistory();

  function get() {
    return defaultsDeep(location.state || {}, initialState);
  }
  function set(state) {
    history.replace({ state });
  }

  return useGlobalState(get, set, reducer);
}

const storageEmitter = mitt();

function useStorageState(key, initialState = {}, storageArea = localStorage) {
  return useStorageReducer(key, partialStateReducer, initialState, storageArea);
}

function useStorageReducer(
  key,
  reducer,
  initialState = {},
  storageArea = localStorage,
) {
  const [val, setVal] = useState(get);

  function get() {
    return defaultsDeep(
      storageArea.getItem(key) ? JSON.parse(storageArea.getItem(key)) : {},
      initialState,
    );
  }
  function set(value) {
    storageArea.setItem(key, JSON.stringify(value));
    storageEmitter.emit('update', { key, storageArea, value });
  }
  function listen() {
    function onStorage(e) {
      if (e.storageArea === storageArea && e.key === key) {
        setVal(get());
      }
    }
    window.addEventListener('storage', onStorage);
    storageEmitter.on('update', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      storageEmitter.off('update', onStorage);
    };
  }

  return useGlobalState(() => val, set, reducer, listen);
}

export default useGlobalState;
export {
  useHistoryState,
  useQueryStringState,
  useHistoryReducer,
  useQueryStringReducer,
  useStorageState,
  useStorageReducer,
};
